import { isString } from 'lodash';
// import { vuex } from '@/store';
import Store from "@/store";
import wretch, { WretcherError } from "wretch";
import { RestResponse } from '@/interfaces/RestResponse';
import { ParamDictionary } from '@/interfaces/ParamDictionary';

export default class GlobalHelper {
  constructor() { }

  parseKeysToValue(obj: any, path = '') {
    for (let key in obj) {
      if (typeof obj[key] === 'string') {
        obj[key] = path + key;
      } else {
        path += `${key}.`;
        this.parseKeysToValue(obj[key], path);
        path = path.replace(`${key}.`, '');
      }
    }
  }

  // OS detection
  get isiOs() {
    return navigator.userAgent.match(/iPhone|iPad|iPod/i);
  }

  get isAndroid () {
    return navigator.userAgent.match(/Android/i);
  }

  // Edge on Android detected by presence of "EdgA" in user agent
  get isEdgeOnAndroid() {
    return this.isAndroid && navigator.userAgent.includes("EdgA");
  }

  get isChromeOnandroid() {
    return this.isAndroid && navigator.userAgent.includes("Chrome");
  }

  get baseUrl() {
    return window.location.host;
  }

  // env
  get isDev() {
    if (window.location.host.startsWith("test.") || window.location.host.startsWith("hessing.digiclass-online.de")) {
      return true;
    }
    return false;
  }

  get isLocal() {
    if (window.location.host.includes("local")) {
      return true;
    }
    return false;
  }

  containsOnlyDigits(str) {
    return !isNaN(str) && !isNaN(parseInt(str));
  }

  // tooltip open delay
  get openDelay(): number {
    return 600;
  }

  // utils
  delay(ms = 100) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  extractFileName(url: string): string {
    if (!url) return '';
    return url.split('/').pop() || '';
  }

  extractFileExtension(fileName: string): string {
    if (!fileName) return '';
    const parts = fileName.split('.');
    if (parts.length <= 1) {
      return '';
    }
    return parts.pop() || '';
  }

  changeFileExtension(filePath: string, newExtension: string): string {
    const parts = filePath.split('.');
    if (parts.length <= 1) {
      return filePath + '.' + newExtension;
    }
    parts.pop(); // remove last element (old extension)
    return parts.join('.') + '.' + newExtension;
  }

  fileSizeFormatted(size: number): string {
    if (!+size) return '0 Bytes'

    const k = 1024;
    const dm = 2;
    const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']

    const i = Math.floor(Math.log(size) / Math.log(k))

    return `${parseFloat((size / Math.pow(k, i)).toFixed(dm)).toLocaleString()} ${sizes[i]}`
  }

  getNewGuid() {
    let guid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.floor(Math.random() * 16);
      const value = c === 'x' ? r : (r % 4 + 8);
      return value.toString(16);
    });
    return guid;
  }

  toPercent(value: number) {
    return (value * 100).toFixed(1) + "%";
  }

  toCurrency(value: number): string {
    let userLang = navigator.language;
    return new Intl.NumberFormat(userLang, { style: 'currency', currency: 'EUR' }).format(value);
  }

  // convert date to local date string with time
  toLocalDateTimeString(date: Date): string {
    if (!date) return '';
    let userLang = navigator.language;
    return new Intl.DateTimeFormat(userLang, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(date));
  }

  // convert date to local date string without time
  toLocalDateString(date: Date): string {
    if (!date) return '';
    let userLang = navigator.language;
    return new Intl.DateTimeFormat(userLang, { dateStyle: 'short' }).format(new Date(date));
  }

  getLanguageNameFromCode(code: string): string {
    switch (code) {
      case "de-DE": return "Deutsch";
      case "en-US": return "Englisch (US)";
      case "en-GB": return "Englisch (UK)";
      case "fr-FR": return "Französisch";
      case "es-ES": return "Spanisch";
      case "it-IT": return "Italienisch";
      case "ja-JP": return "Japanisch";
      case "ko-KR": return "Koreanisch";
      case "nl-NL": return "Niederländisch";
      case "pt-BR": return "Portugiesisch (Brasilien)";
      case "sv-SE": return "Schwedisch";
      case "da-DK": return "Dänisch";
      case "nb-NO": return "Norwegisch (Bokmål)";
      case "nn-NO": return "Norwegisch (Nynorsk)";
      case "fi-FI": return "Finnisch";
      case "pl-PL": return "Polnisch";
      case "cs-CZ": return "Tschechisch";
      case "sk-SK": return "Slowakisch";
      case "hu-HU": return "Ungarisch";
      case "ro-RO": return "Rumänisch";
      case "bg-BG": return "Bulgarisch";
      case "el-GR": return "Griechisch";
      case "ru-RU": return "Russisch";
      case "tr-TR": return "Türkisch";
      case "sl-SI": return "Slowenisch";
      case "hr-HR": return "Kroatisch";
      case "sr-RS": return "Serbisch";
      case "et-EE": return "Estnisch";
      case "lv-LV": return "Lettisch";
      case "lt-LT": return "Litauisch";
      case "is-IS": return "Isländisch";
      default: return code;
    }
  }

  // Language options with country flags
  // Flag images are stored locally in /public/img/flags/{countryCode}.png
  // Downloaded from flagcdn.com (Public Domain / CC0 License):
  // Example: https://flagcdn.com/w80/de.png
  // To add more flags, download from: https://flagcdn.com/w80/{countryCode}.png
  // and save to /public/img/flags/{countryCode}.png
  euLanguageOptions: Array<{ text: string; value: string; flag: string; countryCode: string }> = [
    // { text: 'Albanisch', value: 'sq-AL', flag: '🇦🇱', countryCode: 'al' },
    // { text: 'Baskisch', value: 'eu-ES', flag: '🇪🇸', countryCode: 'es' },
    // { text: 'Bulgarisch', value: 'bg-BG', flag: '🇧🇬', countryCode: 'bg' },
    { text: 'Deutsch', value: 'de-DE', flag: '🇩🇪', countryCode: 'de' },
    // { text: 'Dänisch', value: 'da-DK', flag: '🇩🇰', countryCode: 'dk' },
    { text: 'Englisch (UK)', value: 'en-GB', flag: '🇬🇧', countryCode: 'gb' },
    { text: 'Englisch (US)', value: 'en-US', flag: '🇺🇸', countryCode: 'us' },
    // { text: 'Estnisch', value: 'et-EE', flag: '🇪🇪', countryCode: 'ee' },
    // { text: 'Finnisch', value: 'fi-FI', flag: '🇫🇮', countryCode: 'fi' },
    { text: 'Französisch', value: 'fr-FR', flag: '🇫🇷', countryCode: 'fr' },
    { text: 'Griechisch', value: 'el-GR', flag: '🇬🇷', countryCode: 'gr' },
    { text: 'Italienisch', value: 'it-IT', flag: '🇮🇹', countryCode: 'it' },
    // { text: 'Katalanisch', value: 'ca-ES', flag: '🇪🇸', countryCode: 'es' },
    { text: 'Kroatisch', value: 'hr-HR', flag: '🇭🇷', countryCode: 'hr' },
    // { text: 'Lettisch', value: 'lv-LV', flag: '🇱🇻', countryCode: 'lv' },
    // { text: 'Litauisch', value: 'lt-LT', flag: '🇱🇹', countryCode: 'lt' },
    // { text: 'Maltesisch', value: 'mt-MT', flag: '🇲🇹', countryCode: 'mt' },
    { text: 'Niederländisch', value: 'nl-NL', flag: '🇳🇱', countryCode: 'nl' },
    { text: 'Polnisch', value: 'pl-PL', flag: '🇵🇱', countryCode: 'pl' },
    // { text: 'Portugiesisch', value: 'pt-PT', flag: '🇵🇹', countryCode: 'pt' },
    { text: 'Rumänisch', value: 'ro-RO', flag: '🇷🇴', countryCode: 'ro' },
    // { text: 'Schwedisch', value: 'sv-SE', flag: '🇸🇪', countryCode: 'se' },
    // { text: 'Serbisch', value: 'sr-RS', flag: '🇷🇸', countryCode: 'rs' },
    // { text: 'Slowakisch', value: 'sk-SK', flag: '🇸🇰', countryCode: 'sk' },
    // { text: 'Slowenisch', value: 'sl-SI', flag: '🇸🇮', countryCode: 'si' },
    { text: 'Spanisch', value: 'es-ES', flag: '🇪🇸', countryCode: 'es' },
    { text: 'Tschechisch', value: 'cs-CZ', flag: '🇨🇿', countryCode: 'cz' },
    { text: 'Türkisch', value: 'tr-TR', flag: '🇹🇷', countryCode: 'tr' },
    { text: 'Ungarisch', value: 'hu-HU', flag: '🇭🇺', countryCode: 'hu' },
    // { text: 'Ukrainisch', value: 'uk-UA', flag: '🇺🇦', countryCode: 'ua' },
  ];

  getFlagUrl(countryCode: string): string {
    return `/img/flags/${countryCode}.png`;
  }

  scrollToTop() {
    window.scrollTo(0,0);
  }

  async scrollElementToEnd(id) {
    console.log("scrollElementToEnd: ", id);
    await this.delay(100);

    const element = document.getElementById(id);
    if (!element) return;

    element.scrollTop = element.scrollHeight;
  }

  async animateScrollToBottom(id: string, duration: number) {
    // console.log("animateScrollToBottom: ", id);
    await this.delay(100);

    let element = document.getElementById(id);
    if (!element) return;

    // cancel if already on top
    if (element.scrollTop === element.scrollHeight) return;

    const startPos = element.scrollTop;
    const amountToScroll = element.scrollHeight - startPos;
    const cosParameter = amountToScroll / 2;
    // console.log("cosParameter1: ", cosParameter);
    let scrollCount = 0;
    let oldTimestamp = null;

    function step (newTimestamp) {
      // console.log("newTimestamp: ", newTimestamp);
      if (oldTimestamp !== null) {
        // if duration is 0 scrollCount will be Infinity
        scrollCount += Math.PI * (newTimestamp - oldTimestamp) / duration;
        if (scrollCount >= Math.PI) {
          element!.scrollTop = element!.scrollHeight;
          return;
        }
        // console.log("cosParameter: ", cosParameter);
        // console.log("scrollCount: ", scrollCount);
        // console.log("Math.cos(scrollCount): ", Math.cos(scrollCount));
        element!.scrollTop = startPos + cosParameter - cosParameter * Math.cos(scrollCount);
      }
      oldTimestamp = newTimestamp;
      window.requestAnimationFrame(step);
    }
    window.requestAnimationFrame(step);
  }

  async copyToClipboard(value: string, showValueInMessage: boolean = false) {
    await navigator.clipboard.writeText(value);
    if (showValueInMessage) {
      Store.commit("ux/SB_SUCCESS", {
        message: value + " in Zwischenablage kopiert",
        timeout: 2000
      });
    }
    else {
      Store.commit("ux/SB_SUCCESS", {
        message: "In Zwischenablage kopiert",
        timeout: 2000
      });
    }
  }

  copy(v: string | unknown, interpoled = '') {
    if (!isString(v)) return;
    if (interpoled.includes('v')) {
      v = interpoled.replace('v', v);
    }
    navigator.clipboard.writeText(v as string);
    Store.commit("ux/SB_SUCCESS", {
      message: "In Zwischenablage kopiert",
      timeout: 2000
    });
  }

  downloadFile(path: string) {
    const filename = this.extractFileName(path);
    this.download(path, filename);
  }

  // https://attacomsian.com/blog/javascript-download-file
  download(path: string, filename: string) {
    // Create a new link
    const anchor = document.createElement('a');
    anchor.href = path;
    anchor.download = filename;

    // Append to the DOM
    document.body.appendChild(anchor);

    // Trigger `click` event
    anchor.click();

    // Remove element from DOM
    document.body.removeChild(anchor);
  };

  blobDownloadWithAuth(path: string, filename: string, paramDir: ParamDictionary) {
    let token = window.localStorage.getItem("digiClassAuth");
    wretch(path)
    .auth(`Bearer ${ token }`)
    .post(paramDir)
    .blob(data => {
      // const blob = new Blob([data], { type: 'application/pdf' });
      const blob = new Blob([data]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    })
    .catch(err => {
      let apiError: RestResponse = JSON.parse(err.text);
      Store.commit("ux/SB_FAILURE", {
        message: `Server Error - ${apiError.description}`,
        timeout: 0
      });
    });
  }

  decodeRouteQueryStr(v: string): any | null {
    if (!v.includes('?')) return null;
    let qd = {};
    qd['route'] = v.split('?')[0];
    let queryStr = v.split('?')[1];
    queryStr.split(`&`).forEach(item => {
      let [k, j] = item.split(`=`);
      j = j && decodeURIComponent(j);
      (qd[k] = qd[k] || []).push(j);
    });
    return qd;
  }

  sendMail(addr: string) {
    window.location.href = 'mailto:' + addr;
  }

  // callTo(addr: string) {
  //   window.location.href = 'callto:' + addr;
  // }

  // imWith(addr: string) {
  //   window.location.href = 'im:' + addr;
  // }
}
